import dayjs from 'dayjs';
import {useCallback, useState} from 'react';
import {
  FormControlLabel,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {useQueryClient} from '@tanstack/react-query';
import {useSelector} from 'react-redux';
import {
  IStationWithParent,
  useRqListCompanyStations,
} from '../../../../../react_query/stations/CompanyStationList';
import {useStoreSelector} from '../../../../../store/configstore';
import {IErrorResponse} from '../../../../../dto/Html.dto';
import RetryPage from '../../../../../components/styles/RetryButton';
import {companyIdSelector} from '../../../../../store/Slices/companySlice';
import IconLink from '../../../../../asset/icons/icon_link.svg';
import {UserCanAny} from '../../../../../components/UserCan';
import {DeviceInfoDialog} from './DeviceInfoDialog';
import GenerateStationPin from './GenerateStationPin';
import CustomizedSwitches from '../../../../../components/styles/ToggleButton';
import {apiPost} from '../../../../../axiosConfig';
import DialogApiRequestStatus from '../../../../../components/dialogs/DialogApiRequestStatus';
import TableSkeleton from '../../../../../components/skeleton/TableSkeleton';

const DashboardStaffStation = () => {
  const [, setRetryTime] = useState(Date.now());
  const [selectedStation, setSelectedStation] =
    useState<IStationWithParent | null>(null);
  const companyId = useStoreSelector(companyIdSelector);

  const {
    data: stations,
    isFetching: stationsFetching,
    isError: stationsIsError,
    error: stationsError,
  } = useRqListCompanyStations(
    Number(companyId),
    Boolean(companyId),
    'showMirror',
    '',
    false,
  );

  if (stationsIsError) {
    const errResp = stationsError as IErrorResponse;
    return (
      <RetryPage
        pageTitle='List stations'
        errResp={errResp?.response}
        setRetryTime={setRetryTime}
        queryKey={['api/v1/company', Number(companyId), 'stations']}
      />
    );
  }

  return (
    <>
      {selectedStation && (
        <DeviceInfoDialog
          station={selectedStation}
          setSelectedStation={setSelectedStation}
        />
      )}
      <TableContainer className='h-full overflow-auto scrollbar pr-2'>
        <Table>
          <TableHead sx={{background: `var(--blue_variant27)`}}>
            <TableRow>
              <TableCell>
                <Typography>Type</Typography>
              </TableCell>
              <TableCell>
                <Typography>Name</Typography>
              </TableCell>
              <TableCell>
                <Typography>Player Version</Typography>
              </TableCell>
              <TableCell>
                <Typography>Last Connected</Typography>
              </TableCell>
              <UserCanAny scope='is-admin,station:update'>
                <>
                  <TableCell>
                    <Typography>Paid</Typography>
                  </TableCell>
                  <TableCell align='center'>
                    <Typography>Enable Passcode</Typography>
                  </TableCell>
                </>
              </UserCanAny>
              <TableCell align='right'>
                <Typography>Device Connected</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stationsFetching && <TableSkeleton rows={10} colSpan={5} />}
            {!stationsFetching &&
              stations?.map(station => (
                <StationList
                  station={station}
                  setSelectedStation={setSelectedStation}
                  key={station.id}
                />
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default DashboardStaffStation;

const StationList = ({
  station,
  setSelectedStation,
}: {
  station: IStationWithParent;
  setSelectedStation: React.Dispatch<
    React.SetStateAction<IStationWithParent | null>
  >;
}) => {
  const queryClient = useQueryClient();
  const companyId = useSelector(companyIdSelector);
  const [apiState, setApiState] = useState({
    status: '',
    error: null,
  });

  const handleClick = useCallback(() => {
    setSelectedStation(station);
  }, [setSelectedStation, station]);

  const stationName = station.label || station.name;

  const closeDialog = useCallback(() => {
    setApiState({
      status: '',
      error: null,
    });
  }, []);

  const handleStationUpdate = useCallback(
    (updateData: Record<string, boolean>) => {
      setApiState({status: 'pending', error: null});

      apiPost(
        `v1/company/${companyId}/station/${station.id}?_method=PUT`,
        updateData,
      )
        .then(_response => {
          queryClient.invalidateQueries({
            queryKey: ['api/v1/company', Number(companyId), 'stations'],
            refetchType: 'active',
          });
          setApiState({
            status: 'success',
            error: null,
          });
        })
        .catch(error => {
          setApiState({
            status: 'error',
            error,
          });
        });
    },
    [companyId, queryClient, station.id],
  );

  const getStationType = () => {
    switch (station.type) {
      case 'full_station':
        return 'F';
      case 'mirror_station':
        return 'M';
      case 'odo_station':
        return 'O';
      default:
        return '';
    }
  };

  return (
    <TableRow>
      <TableCell>{getStationType()}</TableCell>
      <TableCell>
        {apiState.status !== '' && (
          <DialogApiRequestStatus
            apiState={apiState}
            onRetry={closeDialog}
            onEdit={closeDialog}
            onClose={closeDialog}
          />
        )}
        {stationName ? (
          <Stack direction={'row'} alignItems={'center'}>
            <StationIsPaidIcon isPaid={station.is_paid} />
            <Typography title={stationName}>{stationName}</Typography>
          </Stack>
        ) : (
          <Skeleton width={80} />
        )}
      </TableCell>
      <TableCell>
        <Typography title={station.player_version}>
          {station.player_version}
        </Typography>
      </TableCell>
      <TableCell>
        {dayjs(station.sync_at).isValid()
          ? dayjs(station.sync_at).format('MM/DD/YYYY hh:mm:ss A')
          : ''}
      </TableCell>
      <UserCanAny scope='is-admin,station:update'>
        <>
          <TableCell align='center'>
            <FormControlLabel
              control={
                <CustomizedSwitches
                  isActive={station.is_paid}
                  onChange={() =>
                    handleStationUpdate({is_paid: !station.is_paid})
                  }
                />
              }
              label=''
              className='text-white'
            />
          </TableCell>
          <TableCell align='center'>
            <FormControlLabel
              control={
                <CustomizedSwitches
                  isActive={station.is_otp_generate}
                  onChange={() =>
                    handleStationUpdate({
                      is_otp_generate: !station.is_otp_generate,
                    })
                  }
                />
              }
              label=''
              className='text-white'
            />
          </TableCell>
        </>
      </UserCanAny>
      <TableCell align='center' className='w-150'>
        <Stack direction='row' spacing={1} justifyContent='center'>
          <UserCanAny scope='is-admin,partner,station:generate-qr'>
            <GenerateStationPin station={station} />
          </UserCanAny>
          <UserCanAny scope='is-admin,station:update,station:delete,generate-qr,player-devices:list,station:credentials'>
            <IconButton
              aria-label='delete'
              onClick={handleClick}
              className='w-30 h-30 bg-dark radius-5'
              sx={{border: `1px solid var(--gray-variant14)`}}>
              <img src={IconLink} alt='station-edit' />
            </IconButton>
          </UserCanAny>
        </Stack>
      </TableCell>
    </TableRow>
  );
};

export const StationIsPaidIcon = ({
  isPaid,
  className,
}: {
  isPaid: boolean;
  className?: string;
}) => {
  const classes = className ? className : 'mr-1 w-35';
  return (
    <img
      src={isPaid ? '/assets/paid.png' : '/assets/not_paid.png'}
      alt=''
      className={classes}
    />
  );
};
